// Hub ▸ Marketing charts — hand-rolled SVG / HTML, no chart library. Colours
// validated with the dataviz palette checker on the white panel surface
// (CVD ΔE 24.7, both ≥ 3:1):  SPEND = orange #eb6834 (dashed line),
// GP = blue #2a78d6 (solid line) — the dash tells them apart without colour.
// One y-axis (both are dollars). Text stays in ink tokens; every chart has a
// hover / keyboard readout announced to screen readers, and its numbers are in
// a table on the same screen.
import { useEffect, useRef, useState } from "react";
import { money, MONTHS } from "./kit";

export const SERIES = { spend: "#eb6834", gp: "#2a78d6" } as const;
const GRID = "#E6E6E1";
const AXIS = "#CFCFCA";
const MUTED = "#666666";
const RISK = "#993A32";
export const SPEND_DASH = "6 4";

export function Legend({ items }: { items: Array<{ label: string; color: string; shape?: "line" | "dashed" | "bar" | "tick" | "hatch" }> }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-1 text-label text-ink">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-2">
          {i.shape === "line" ? <span aria-hidden className="h-0.5 w-6 rounded" style={{ background: i.color }} />
            : i.shape === "dashed" ? <svg aria-hidden width="24" height="4"><line x1="0" y1="2" x2="24" y2="2" stroke={i.color} strokeWidth="2" strokeDasharray={SPEND_DASH} /></svg>
            : i.shape === "tick" ? <span aria-hidden className="h-4 w-0.5 rounded" style={{ background: i.color }} />
            : i.shape === "hatch" ? <span aria-hidden className="h-3 w-4 rounded-sm" style={{ background: `repeating-linear-gradient(135deg, ${i.color} 0 3px, #fff 3px 5px)` }} />
            : <span aria-hidden className="h-3 w-3 rounded-sm" style={{ background: i.color }} />}
          {i.label}
        </li>
      ))}
    </ul>
  );
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setW(Math.max(280, Math.round(e[0]!.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Clean axis ticks: 0 … max in 4–5 round steps. */
export function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  if (out[out.length - 1]! < max) out.push(out[out.length - 1]! + step);
  return out;
}
const dayLabel = (iso: string) => `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}`;
/** A week of the trend: ad spend and the money line — gross profit (admin + finance) or revenue (marketing-only viewers). */
export interface TrendPoint { week: string; from: string; to: string; days: number; partial: boolean; spendCents: number; gpCents?: number | null; revenueCents?: number }

/** Weekly ad spend (dashed) vs gross profit — or revenue, for the revenue view
 *  (`word`) — from marketing (solid). Partial weeks (cut by the dates) are
 *  shaded and labelled; each week is labelled by its first day inside the
 *  period; line ends carry their value. */
export function TrendChart({ data: raw, word = "gross profit" }: { data: TrendPoint[]; word?: string }) {
  const showGp = raw.some((d) => (d.gpCents ?? d.revenueCents ?? null) !== null);
  const data = raw.map((d) => ({ ...d, gpCents: d.gpCents ?? d.revenueCents ?? 0 }));
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const narrow = width < 520;
  const H = narrow ? 240 : 280; const padL = 60; const padR = narrow ? 16 : 128; const padT = 16; const padB = 44;
  const max = Math.max(1, ...data.flatMap((d) => [d.spendCents, d.gpCents]));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const iw = width - padL - padR; const ih = H - padT - padB;
  const x = (i: number) => padL + (data.length <= 1 ? iw / 2 : (i / (data.length - 1)) * iw);
  const y = (v: number) => padT + ih - (v / top) * ih;
  const path = (k: "spendCents" | "gpCents") => data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[k]).toFixed(1)}`).join(" ");
  const area = data.length ? `${path("gpCents")} L${x(data.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z` : "";
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(iw / 80))));
  const step = data.length > 1 ? iw / (data.length - 1) : iw;
  function onMove(e: React.PointerEvent<SVGRectElement>) {
    const r = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    const i = data.length <= 1 ? 0 : Math.round(((e.clientX - r.left) / r.width) * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, i)));
  }
  const h = hover !== null ? data[hover] : null;
  const last = data[data.length - 1];
  // End labels: keep them apart when the lines finish close together.
  let gpY = last ? y(last.gpCents) : 0; let spY = last ? y(last.spendCents) : 0;
  if (Math.abs(gpY - spY) < 34) { const mid = (gpY + spY) / 2; const up = gpY <= spY; gpY = mid + (up ? -17 : 17); spY = mid + (up ? 17 : -17); }
  const readout = h ? `Week from ${dayLabel(h.from)}${h.partial ? ` (partial, ${h.days} day${h.days === 1 ? "" : "s"})` : ""}: ${word} from marketing ${money(h.gpCents)}, ad spend ${money(h.spendCents)}.` : "";
  return (
    <div ref={ref} className="relative">
      <p className="sr-only" aria-live="polite">{readout}</p>
      <svg width={width} height={H} role="img" aria-label={`Weekly ad spend and ${word} from marketing. Use the left and right arrow keys to read each week.`} className="block overflow-visible"
        onKeyDown={(e) => { if (e.key === "ArrowRight") { e.preventDefault(); setHover((v) => Math.min(data.length - 1, (v ?? -1) + 1)); } if (e.key === "ArrowLeft") { e.preventDefault(); setHover((v) => Math.max(0, (v ?? 1) - 1)); } }}
        tabIndex={0} onBlur={() => setHover(null)}>
        {data.map((d, i) => d.partial && (
          <rect key={`p${d.week}`} x={Math.max(padL, x(i) - step / 2)} y={padT} width={Math.min(step, iw) / (data.length <= 1 ? 1 : 1)} height={ih} fill="#F7F7F4" />
        ))}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke={t === 0 ? AXIS : GRID} strokeWidth={1} />
            <text x={padL - 10} y={y(t)} dy="0.32em" textAnchor="end" fontSize={13} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>{money(t, { compact: true })}</text>
          </g>
        ))}
        {data.map((d, i) => (i % labelEvery === 0 || i === data.length - 1) && (
          <g key={d.week}>
            <text x={x(i)} y={H - 24} textAnchor={data.length === 1 ? "middle" : i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"} fontSize={13} fill={MUTED}>{dayLabel(d.from)}</text>
            {d.partial && <text x={x(i)} y={H - 6} textAnchor={data.length === 1 ? "middle" : i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"} fontSize={12} fill={MUTED}>partial week ({d.days} day{d.days === 1 ? "" : "s"})</text>}
          </g>
        ))}
        {showGp && <path d={area} fill={SERIES.gp} opacity={0.08} />}
        <path d={path("spendCents")} fill="none" stroke={SERIES.spend} strokeWidth={2} strokeDasharray={SPEND_DASH} strokeLinejoin="round" strokeLinecap="round" />
        {showGp && <path d={path("gpCents")} fill="none" stroke={SERIES.gp} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
        {data.map((d, i) => ((showGp ? ["spendCents", "gpCents"] : ["spendCents"]) as Array<"spendCents" | "gpCents">).map((k) => (
          <circle key={`${k}${i}`} cx={x(i)} cy={y(d[k])} r={d.partial ? 4 : 4.5} fill={d.partial ? "#fff" : k === "gpCents" ? SERIES.gp : SERIES.spend} stroke={k === "gpCents" ? SERIES.gp : SERIES.spend} strokeWidth={2} />
        )))}
        {last && !narrow && (
          <g fontSize={13} fill="#080808">
            {showGp && <text x={x(data.length - 1) + 10} y={gpY} dy="0.32em"><tspan fontWeight={600}>{money(last.gpCents)}</tspan><tspan fill={MUTED}> {word}</tspan></text>}
            <text x={x(data.length - 1) + 10} y={spY} dy="0.32em"><tspan fontWeight={600}>{money(last.spendCents)}</tspan><tspan fill={MUTED}> ad spend</tspan></text>
          </g>
        )}
        {h && hover !== null && <line x1={x(hover)} x2={x(hover)} y1={padT} y2={y(0)} stroke={MUTED} strokeWidth={1} pointerEvents="none" />}
        <rect x={padL} y={padT} width={iw} height={ih} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {h && hover !== null && (
        <div className="pointer-events-none absolute top-2 z-10 min-w-[210px] rounded-md border border-rule bg-white px-3 py-2 text-label shadow-md"
          style={{ left: Math.min(Math.max(0, x(hover) - 105), width - 220) }} aria-hidden>
          <p className="text-meta text-ink-muted">Week from {dayLabel(h.from)}{h.partial ? ` · partial (${h.days} day${h.days === 1 ? "" : "s"})` : ""}</p>
          <p className="mt-1 flex items-center gap-2"><span className="h-0.5 w-4" style={{ background: SERIES.gp }} /><span className="font-semibold tabular-nums">{money(h.gpCents)}</span><span className="text-ink-muted">{word}</span></p>
          <p className="flex items-center gap-2"><svg width="16" height="4"><line x1="0" y1="2" x2="16" y2="2" stroke={SERIES.spend} strokeWidth="2" strokeDasharray="4 3" /></svg><span className="font-semibold tabular-nums">{money(h.spendCents)}</span><span className="text-ink-muted">ad spend</span></p>
        </div>
      )}
    </div>
  );
}

/**
 * Bullet bar: the filled bar is what was spent, the ink tick is what was
 * expected by now (or the plan), the light track is the whole budget. Over
 * pace is red AND hatched (a marker that doesn't rely on colour), and the
 * label says the flag in words.
 */
export function Bullet({ value, target, track, over, label }: { value: number; target: number | null; track: number; over: boolean; label: string }) {
  const max = Math.max(1, track, value, target ?? 0);
  const pct = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  return (
    <div className="relative h-4 w-full min-w-[120px] rounded-[3px] bg-paper" role="img" aria-label={label}>
      {track > 0 && <div className="absolute inset-y-0 left-0 rounded-[3px] bg-[#E6E6E1]" style={{ width: pct(track) }} />}
      <div className="absolute inset-y-[3px] left-0 rounded-r-[4px]"
        style={{ width: pct(value), background: over ? `repeating-linear-gradient(135deg, ${RISK} 0 4px, #C46F67 4px 7px)` : SERIES.spend }} />
      {target !== null && target > 0 && <div className="absolute -inset-y-1 w-[2px] rounded bg-ink" style={{ left: `calc(${pct(target)} - 1px)` }} />}
    </div>
  );
}

// ── slice S3 ────────────────────────────────────────────────────────────────

/** Sequential blue (the dataviz reference ramp, one hue, light → dark: run
 *  through validate_palette — monotone, single hue). Light = low, dark = high;
 *  every cell also prints its value, so the colour is never the only cue. Text
 *  on the two darkest steps is white (≥ 5:1), ink on the rest (≥ 5:1). */
export const HEAT = ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#104281"] as const;
export function heat(t: number | null): { bg: string; fg: string } | null {
  if (t === null || !Number.isFinite(t)) return null;
  const i = Math.max(0, Math.min(HEAT.length - 1, Math.floor(t * HEAT.length)));
  return { bg: HEAT[i]!, fg: i >= 4 ? "#FFFFFF" : "#080808" };
}
/** "Still filling in": a hatched fill — a marker that doesn't rely on colour. */
export const MATURING_FILL = `repeating-linear-gradient(135deg, ${SERIES.gp} 0 4px, #9ec5f4 4px 8px)`;

export interface FunnelBarStage { id: string; label: string; count: number; ofStart: number | null; fromPrev: number | null; medianDaysFromPrev: number | null; maturing: boolean }
/**
 * The journey funnel as horizontal bars (one measure, one hue): each bar is a
 * stage's count against the first stage; between bars, the conversion from the
 * stage before and the median days it took. A stage still filling in is
 * hatched and says so in words. Each bar opens the journeys behind it.
 */
export function FunnelBars({ stages, onOpen, biggestDrop = null, repeatReady = true }: { stages: FunnelBarStage[]; onOpen: (id: string, label: string) => void; biggestDrop?: string | null; repeatReady?: boolean }) {
  const top = Math.max(1, stages[0]?.count ?? 1);
  const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v * 100)}%`);
  return (
    <ol className="space-y-1">
      {stages.map((s, i) => {
        const tooYoung = s.id === "repeat" && !repeatReady;
        return (
        <li key={s.id}>
          {i > 0 && (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1.5 pl-1 text-body text-ink-muted sm:pl-[232px]">
              <span aria-hidden>↓</span>
              {tooYoung ? <span className="text-label">Repeat orders can't be judged until these months are a year old.</span> : (
                <span><span className="font-semibold text-ink">{pct(s.fromPrev)}</span> {i === 1 ? "got a quote" : i === 2 ? "of those placed a paid order" : "of those ordered again within 12 months"}</span>
              )}
              {!tooYoung && s.medianDaysFromPrev !== null && <span className="text-label">· median {Math.round(s.medianDaysFromPrev)} day{Math.round(s.medianDaysFromPrev) === 1 ? "" : "s"}</span>}
              {biggestDrop === s.id && <span className="inline-flex items-center gap-1.5 rounded bg-risk-bg px-2 py-0.5 text-meta font-semibold text-risk"><span aria-hidden>▼</span>Biggest drop</span>}
            </p>
          )}
          <button type="button" onClick={() => onOpen(s.id, s.label)}
            aria-label={`${s.label}: ${tooYoung ? "too young to judge" : s.count.toLocaleString("en-US")}${i > 0 && !tooYoung ? `, ${pct(s.ofStart)} of first contacts` : ""}${s.maturing ? ", still filling in" : ""}${biggestDrop === s.id ? ", the biggest drop" : ""}. See the journeys`}
            className="group grid w-full grid-cols-1 items-center gap-1 rounded-md px-1 py-1.5 text-left hover:bg-[#FAFAF8] sm:grid-cols-[220px_1fr] sm:gap-3">
            <span className="text-label font-semibold text-ink">{s.label}{s.maturing && <span className="ml-2 inline-block align-middle text-meta font-normal text-warn">still filling in</span>}</span>
            <span className="flex items-center gap-3">
              <span className="relative h-9 flex-1 rounded-[3px] bg-paper">
                <span className="absolute inset-y-0 left-0 rounded-r-[4px]" style={{ width: `${tooYoung ? 0 : Math.max(s.count ? 1.5 : 0, (s.count / top) * 100)}%`, background: s.maturing ? MATURING_FILL : SERIES.gp }} />
              </span>
              <span className="w-[150px] shrink-0 text-right">
                <span className="block text-[24px] font-semibold leading-none tabular-nums text-ink">{tooYoung ? "—" : s.count.toLocaleString("en-US")}</span>
                <span className="block text-meta text-ink-muted">{i === 0 ? "journeys" : tooYoung ? "too young to judge" : `${pct(s.ofStart)} of first contacts`}</span>
              </span>
            </span>
          </button>
        </li>
        );
      })}
    </ol>
  );
}

// ── slice S4: Explore ───────────────────────────────────────────────────────
// One measure at a time, so ONE hue: the chosen period is the validated blue
// (SERIES.gp); the comparison period is a recessive grey, DASHED (line) or a
// thin TICK (bars) — context, not a second identity, so the dash / tick carries
// it, never colour alone. A legend names both.
export const COMPARE_GREY = "#8A8A85";
export interface ExplorePoint { key: string; label: string; sublabel?: string | null; value: number | null; compareValue: number | null }

/** Clean ticks across [lo, hi] (lo may be negative). */
function rangeTicks(lo: number, hi: number): number[] {
  if (lo >= 0) return niceTicks(hi);
  const span = Math.max(1e-9, hi - lo);
  const raw = span / 4; const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.001; v += step) out.push(Math.round(v / step) * step);
  if (out[out.length - 1]! < hi) out.push(out[out.length - 1]! + step);
  return out;
}

/** Change over time: the chosen period (solid blue) and the comparison (dashed grey), crosshair + tooltip, arrow keys read each point. */
export function SeriesLine({ data, fmt, name, compareLabel }: { data: ExplorePoint[]; fmt: (v: number | null) => string; name: string; compareLabel: string | null }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const narrow = width < 520;
  const H = narrow ? 230 : 270; const padL = 64; const padR = 16; const padT = 14; const padB = 34;
  const vals = data.flatMap((d) => [d.value, d.compareValue]).filter((v): v is number => v !== null);
  const lo = Math.min(0, ...vals); const hi = Math.max(lo + 1e-9, ...vals, 0);
  const ticks = rangeTicks(lo, hi === 0 && lo === 0 ? 1 : hi);
  const top = ticks[ticks.length - 1]!; const bottom = ticks[0]!;
  const iw = width - padL - padR; const ih = H - padT - padB;
  const x = (i: number) => padL + (data.length <= 1 ? iw / 2 : (i / (data.length - 1)) * iw);
  const y = (v: number) => padT + ih - ((v - bottom) / (top - bottom || 1)) * ih;
  const path = (k: "value" | "compareValue") => {
    let d = ""; let pen = false;
    data.forEach((p, i) => { const v = p[k]; if (v === null) { pen = false; return; } d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `; pen = true; });
    return d.trim();
  };
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(iw / 84))));
  const h = hover !== null ? data[hover] : null;
  const readout = h ? `${h.label}: ${fmt(h.value)}${compareLabel ? `, ${compareLabel} ${fmt(h.compareValue)}` : ""}.` : "";
  const hasCompare = !!compareLabel && data.some((d) => d.compareValue !== null);
  function onMove(e: React.PointerEvent<SVGRectElement>) {
    const r = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    const i = data.length <= 1 ? 0 : Math.round(((e.clientX - r.left) / r.width) * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, i)));
  }
  return (
    <div ref={ref} className="relative">
      <div className="mb-2"><Legend items={[{ label: name, color: SERIES.gp, shape: "line" }, ...(hasCompare ? [{ label: compareLabel!, color: COMPARE_GREY, shape: "dashed" as const }] : [])]} /></div>
      <p className="sr-only" aria-live="polite">{readout}</p>
      <svg width={width} height={H} role="img" aria-label={`${name} over time. Use the left and right arrow keys to read each point.`} className="block overflow-visible" tabIndex={0} onBlur={() => setHover(null)}
        onKeyDown={(e) => { if (e.key === "ArrowRight") { e.preventDefault(); setHover((v) => Math.min(data.length - 1, (v ?? -1) + 1)); } if (e.key === "ArrowLeft") { e.preventDefault(); setHover((v) => Math.max(0, (v ?? 1) - 1)); } }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke={t === 0 ? AXIS : GRID} strokeWidth={1} />
            <text x={padL - 10} y={y(t)} dy="0.32em" textAnchor="end" fontSize={13} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(t)}</text>
          </g>
        ))}
        {data.map((d, i) => (i % labelEvery === 0 || i === data.length - 1) && (
          <text key={d.key} x={x(i)} y={H - 10} textAnchor={data.length === 1 ? "middle" : i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"} fontSize={13} fill={MUTED}>{d.label}</text>
        ))}
        {hasCompare && <path d={path("compareValue")} fill="none" stroke={COMPARE_GREY} strokeWidth={2} strokeDasharray={SPEND_DASH} strokeLinejoin="round" strokeLinecap="round" />}
        <path d={path("value")} fill="none" stroke={SERIES.gp} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => d.value !== null && <circle key={d.key} cx={x(i)} cy={y(d.value)} r={4.5} fill={SERIES.gp} stroke="#fff" strokeWidth={2} />)}
        {h && hover !== null && <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + ih} stroke={MUTED} strokeWidth={1} pointerEvents="none" />}
        <rect x={padL} y={padT} width={Math.max(1, iw)} height={ih} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {h && hover !== null && (
        <div className="pointer-events-none absolute top-8 z-10 min-w-[200px] rounded-md border border-rule bg-white px-3 py-2 text-label shadow-md" style={{ left: Math.min(Math.max(0, x(hover) - 100), width - 210) }} aria-hidden>
          <p className="text-meta text-ink-muted">{h.label}</p>
          <p className="mt-1 flex items-center gap-2"><span className="h-0.5 w-4" style={{ background: SERIES.gp }} /><span className="font-semibold tabular-nums">{fmt(h.value)}</span></p>
          {hasCompare && <p className="flex items-center gap-2"><svg width="16" height="4"><line x1="0" y1="2" x2="16" y2="2" stroke={COMPARE_GREY} strokeWidth="2" strokeDasharray="4 3" /></svg><span className="tabular-nums">{fmt(h.compareValue)}</span><span className="text-ink-muted">{compareLabel}</span></p>}
        </div>
      )}
    </div>
  );
}

/** Categories: one horizontal bar per row (blue), the comparison as a grey tick, the value printed at the end. Hover / focus a row for its readout. */
export function CategoryBars({ data, fmt, name, compareLabel, max = 12, marker }: { data: ExplorePoint[]; fmt: (v: number | null) => string; name: string; compareLabel: string | null; max?: number; marker?: { value: number; label: string } }) {
  const [hover, setHover] = useState<string | null>(null);
  const rows = data.slice(0, max);
  const top = Math.max(1e-9, marker ? marker.value * 1.1 : 0, ...rows.flatMap((d) => [Math.abs(d.value ?? 0), Math.abs(d.compareValue ?? 0)]));
  const pct = (v: number) => `${Math.min(100, (Math.abs(v) / top) * 100)}%`;
  const hasCompare = !!compareLabel && rows.some((d) => d.compareValue !== null);
  return (
    <div>
      <div className="mb-3"><Legend items={[{ label: name, color: SERIES.gp, shape: "bar" }, ...(hasCompare ? [{ label: compareLabel!, color: COMPARE_GREY, shape: "tick" as const }] : []), ...(marker ? [{ label: marker.label, color: "#080808", shape: "tick" as const }] : [])]} /></div>
      <ul className="space-y-1.5" aria-label={`${name} by row`}>
        {rows.map((d) => (
          <li key={d.key} tabIndex={0} onMouseEnter={() => setHover(d.key)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(d.key)} onBlur={() => setHover(null)}
            className="grid grid-cols-[minmax(0,1fr)] items-center gap-1 rounded-md px-1 py-1 outline-none hover:bg-[#FAFAF8] focus-visible:ring-2 focus-visible:ring-focus sm:grid-cols-[200px_minmax(0,1fr)_110px] sm:gap-3"
            aria-label={`${d.label}: ${fmt(d.value)}${hasCompare ? `; ${compareLabel} ${fmt(d.compareValue)}` : ""}`}>
            <span className="min-w-0"><span className="block truncate text-label text-ink" title={d.label}>{d.label}</span>{d.sublabel && <span className="block truncate text-meta text-ink-muted">{d.sublabel}</span>}</span>
            <span className="relative h-6 rounded-[3px] bg-paper">
              {d.value !== null && <span className="absolute inset-y-0 left-0 rounded-r-[4px]" style={{ width: pct(d.value), minWidth: d.value ? 3 : 0, background: d.value < 0 ? RISK : SERIES.gp }} />}
              {hasCompare && d.compareValue !== null && <span className="absolute -inset-y-1 w-[2px] rounded" style={{ left: `calc(${pct(d.compareValue)} - 1px)`, background: COMPARE_GREY }} />}
              {marker && <span aria-hidden className="absolute -inset-y-1.5 w-[2px] rounded bg-ink" style={{ left: `calc(${pct(marker.value)} - 1px)` }} />}
            </span>
            <span className="text-right text-label font-semibold tabular-nums text-ink">{fmt(d.value)}</span>
            {hover === d.key && hasCompare && <span className="text-meta text-ink-muted sm:col-start-2">{compareLabel}: {fmt(d.compareValue)}</span>}
          </li>
        ))}
      </ul>
      {data.length > max && <p className="mt-2 text-meta text-ink-muted">The chart shows the top {max}; the table below has all {data.length} rows.</p>}
    </div>
  );
}
